/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Download, FileText, CheckCircle, XCircle, Clock,
  AlertTriangle, Send, Ban, DollarSign, Calendar, Building, Mail,
  Phone, MapPin, Printer, RefreshCw, Briefcase, Hash, Landmark
} from 'lucide-react';
import {
  useInvoice,
  useIssueInvoice,
  useCancelInvoice,
  useMarkInvoicePaid,
  useGenerateInvoicePdf,
  useDownloadInvoicePdf,
  useDownloadInvoicePdfVariant,
  useCheckInvoicePaymentStatus,
} from '@/hooks/api/useInvoices';
import { InvoiceStatus, InvoiceLineItem } from '@/types/superadmin.types';
import { isApiError, formatErrorForDisplay } from '@/services/api/errors';
import { InvoicePreviewModal, PrintInvoiceDialog } from '@/components/billing';

// Status configuration
const statusConfig: Record<InvoiceStatus, { bg: string; text: string; icon: typeof CheckCircle; labelKey: string }> = {
  DRAFT: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: FileText, labelKey: 'invoices.status.draft' },
  ISSUED: { bg: 'bg-blue-500/15', text: 'text-blue-500', icon: Send, labelKey: 'invoices.status.issued' },
  PAID: { bg: 'bg-emerald-500/15', text: 'text-emerald-500', icon: CheckCircle, labelKey: 'invoices.status.paid' },
  CANCELLED: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: XCircle, labelKey: 'invoices.status.cancelled' },
  OVERDUE: { bg: 'bg-red-500/15', text: 'text-red-500', icon: AlertTriangle, labelKey: 'invoices.status.overdue' },
  VOID: { bg: 'bg-purple-500/15', text: 'text-purple-500', icon: Ban, labelKey: 'invoices.status.void' },
};

// Status Badge
const StatusBadge: React.FC<{ status: InvoiceStatus; t: (key: string) => string }> = ({ status, t }) => {
  const { bg, text, icon: Icon, labelKey } = statusConfig[status] || statusConfig.DRAFT;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${bg} ${text}`}>
      <Icon className="w-4 h-4" />
      {t(labelKey)}
    </span>
  );
};

// Detail Skeleton
const DetailSkeleton: React.FC = () => (
  <div className="space-y-6 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="h-8 w-48 bg-muted/20 rounded"></div>
      <div className="h-8 w-24 bg-muted/20 rounded"></div>
    </div>
    <div className="grid grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="h-4 w-24 bg-muted/20 rounded"></div>
        <div className="h-4 w-48 bg-muted/20 rounded"></div>
        <div className="h-4 w-36 bg-muted/20 rounded"></div>
      </div>
      <div className="space-y-4">
        <div className="h-4 w-24 bg-muted/20 rounded"></div>
        <div className="h-4 w-48 bg-muted/20 rounded"></div>
        <div className="h-4 w-36 bg-muted/20 rounded"></div>
      </div>
    </div>
    <div className="h-48 bg-muted/20 rounded"></div>
  </div>
);

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('platform');

  const { data: invoice, isLoading, error, refetch } = useInvoice(id || '');
  const issueMutation = useIssueInvoice();
  const cancelMutation = useCancelInvoice();
  const markPaidMutation = useMarkInvoicePaid();
  const generatePdfMutation = useGenerateInvoicePdf();
  const downloadPdfMutation = useDownloadInvoicePdf();
  const downloadPdfVariant = useDownloadInvoicePdfVariant();
  const checkStatusMutation = useCheckInvoicePaymentStatus();

  const [actionError, setActionError] = React.useState<string | null>(null);
  const [showPreview, setShowPreview] = React.useState(false);
  const [showPrintDialog, setShowPrintDialog] = React.useState(false);
  const [showVariantMenu, setShowVariantMenu] = React.useState(false);

  // Available download variants
  const downloadVariants = [
    { code: 'tactical' as const, name: 'Tactical (Balanced)', description: 'Best for digital viewing' },
    { code: 'blackops' as const, name: 'BlackOps (Dark)', description: 'Dark theme with red accents' },
    { code: 'hybrid' as const, name: 'Hybrid (Light)', description: 'Professional light design' },
    { code: 'stealth' as const, name: 'Stealth (Print)', description: 'Minimal B&W for printing' },
  ];

  // Close variant menu when clicking outside
  React.useEffect(() => {
    if (!showVariantMenu) return;
    const handleClickOutside = () => setShowVariantMenu(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showVariantMenu]);

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number | undefined, currency: string = 'USD') => {
    if (amount === undefined) return '-';
    if (currency === 'USD') return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    if (currency === 'GNF') return `${amount.toLocaleString()} FG`;
    if (currency === 'XOF' || currency === 'XAF') return `${amount.toLocaleString()} CFA`;
    return `${amount.toLocaleString()} ${currency}`;
  };

  const handleIssue = async () => {
    if (!invoice) return;
    setActionError(null);
    try {
      await issueMutation.mutateAsync({ id: invoice.id });
    } catch (err) {
      setActionError(isApiError(err) ? formatErrorForDisplay(err, t).message : t('common.error'));
    }
  };

  const handleCancel = async () => {
    if (!invoice) return;
    const reason = window.prompt(t('invoices.cancelReason'));
    if (reason === null) return;
    setActionError(null);
    try {
      await cancelMutation.mutateAsync({ id: invoice.id, reason });
    } catch (err) {
      setActionError(isApiError(err) ? formatErrorForDisplay(err, t).message : t('common.error'));
    }
  };

  const handleMarkPaid = async () => {
    if (!invoice) return;
    setActionError(null);
    try {
      await markPaidMutation.mutateAsync({ id: invoice.id });
    } catch (err) {
      setActionError(isApiError(err) ? formatErrorForDisplay(err, t).message : t('common.error'));
    }
  };

  const handleGeneratePdf = async () => {
    if (!invoice) return;
    setActionError(null);
    try {
      await generatePdfMutation.mutateAsync(invoice.id);
    } catch (err) {
      setActionError(isApiError(err) ? formatErrorForDisplay(err, t).message : t('common.error'));
    }
  };

  const handleDownloadPdf = async (variant: 'blackops' | 'hybrid' | 'tactical' | 'stealth' = 'tactical') => {
    if (!invoice) return;
    setActionError(null);
    setShowVariantMenu(false);
    try {
      const language = t('common.language') === 'fr' ? 'fr' : 'en';
      const filename = invoice.invoiceNumber ? `${invoice.invoiceNumber}.pdf` : undefined;
      await downloadPdfVariant.mutateAsync({
        id: invoice.id,
        variant,
        language,
        filename,
      });
    } catch (err) {
      setActionError(isApiError(err) ? formatErrorForDisplay(err, t).message : t('common.error'));
    }
  };

  const handleCheckStatus = async () => {
    if (!invoice || !invoice.paymentId) return;
    setActionError(null);
    try {
      const result = await checkStatusMutation.mutateAsync({
        invoiceId: invoice.id,
        paymentId: invoice.paymentId,
      });
      // Show success message with the status
      if (result.status) {
        // The invoice will be refreshed automatically via the onSuccess callback
        // We could optionally show a toast here
      }
    } catch (err) {
      setActionError(isApiError(err) ? formatErrorForDisplay(err, t).message : t('common.error'));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </button>
        <DetailSkeleton />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </button>
        <div className="flex flex-col items-center justify-center py-12 text-muted">
          <FileText className="w-12 h-12 mb-3 opacity-50" />
          <p>{t('invoices.notFound')}</p>
        </div>
      </div>
    );
  }

  const isActionLoading = issueMutation.isPending || cancelMutation.isPending || markPaidMutation.isPending || generatePdfMutation.isPending || downloadPdfMutation.isPending || checkStatusMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button onClick={() => navigate('/superadmin/invoices')} className="flex items-center gap-2 text-muted hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> {t('invoices.backToList')}
      </button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
            <FileText className="w-6 h-6" />
            {invoice.invoiceNumber || t('invoices.draftInvoice')}
          </h1>
          {invoice.billingPeriodDisplay && (
            <p className="text-sm text-muted mt-1">{invoice.billingPeriodDisplay}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={invoice.status} t={t} />
          <button
            onClick={() => refetch()}
            className="p-2 rounded-lg border border-border hover:bg-hover"
            title={t('common.refresh')}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Error message */}
      {actionError && (
        <div className="bg-red-500/15 border border-red-500/30 text-red-500 px-4 py-3 rounded-lg">
          {actionError}
        </div>
      )}

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Invoice details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Amount card */}
          <div className="bg-surface border border-border-subtle rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-muted">{t('invoices.totalAmount')}</span>
              {invoice.isOverdue && (
                <span className="text-xs font-semibold text-red-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {t('invoices.overdue')}
                </span>
              )}
            </div>
            <div className="font-mono text-4xl font-bold text-foreground mb-2">
              {/* Show local currency amount if available and not USD, otherwise show USD */}
              {invoice.totalLocal && invoice.billingCurrency && invoice.billingCurrency !== 'USD'
                ? formatCurrency(invoice.totalLocal, invoice.billingCurrency)
                : formatCurrency(invoice.totalUsd, 'USD')}
            </div>
            {invoice.totalLocal && invoice.billingCurrency !== 'USD' && (
              <div className="text-sm text-muted">
                ≈ ${invoice.totalUsd?.toLocaleString(undefined, { minimumFractionDigits: 2 })} USD
                {invoice.exchangeRate && ` (Rate: ${invoice.exchangeRate})`}
              </div>
            )}
          </div>

          {/* Line items */}
          <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-border-subtle">
              <h3 className="font-semibold text-foreground">{t('invoices.lineItems')}</h3>
            </div>
            {invoice.lineItems && invoice.lineItems.length > 0 ? (
              <div className="divide-y divide-border-subtle">
                {invoice.lineItems.map((item: InvoiceLineItem, idx: number) => (
                  <div key={item.id || idx} className="px-6 py-4 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-foreground">{item.description}</div>
                      {item.moduleCode && (
                        <div className="text-xs text-muted mt-1">
                          {item.moduleCode} / {item.tierCode}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      {item.quantity && item.quantity > 1 && (
                        <div className="text-xs text-muted mb-1">
                          {item.quantity} x {formatCurrency(item.unitPriceUsd)}
                        </div>
                      )}
                      <div className="font-mono font-semibold text-foreground">
                        {formatCurrency(item.lineTotalUsd)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-8 text-center text-muted">
                {t('invoices.noLineItems')}
              </div>
            )}
            {/* Totals */}
            <div className="px-6 py-4 bg-background border-t border-border-subtle">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted">{t('invoices.subtotal')}</span>
                <span className="font-mono text-foreground">{formatCurrency(invoice.subtotalUsd)}</span>
              </div>
              {(invoice.taxAmountUsd ?? 0) > 0 && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted">{t('invoices.tax')} ({invoice.taxRate}%)</span>
                  <span className="font-mono text-foreground">{formatCurrency(invoice.taxAmountUsd)}</span>
                </div>
              )}
              {(invoice.discountAmountUsd ?? 0) > 0 && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted">{t('invoices.discount')}</span>
                  <span className="font-mono text-emerald-500">-{formatCurrency(invoice.discountAmountUsd)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-semibold pt-2 border-t border-border-subtle">
                <span className="text-foreground">{t('invoices.total')}</span>
                <span className="font-mono text-foreground">{formatCurrency(invoice.totalUsd)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="bg-surface border border-border-subtle rounded-xl p-6">
              <h3 className="font-semibold text-foreground mb-2">{t('invoices.notes')}</h3>
              <p className="text-sm text-muted whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          )}
        </div>

        {/* Right: Customer info and actions */}
        <div className="space-y-6">
          {/* Actions */}
          <div className="bg-surface border border-border-subtle rounded-xl p-6 space-y-3">
            <h3 className="font-semibold text-foreground mb-4">{t('invoices.actionsTitle')}</h3>

            {/* Issue button (for drafts) */}
            {invoice.canIssue && (
              <button
                onClick={handleIssue}
                disabled={isActionLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
              >
                <Send className="w-4 h-4" /> {t('invoices.issue')}
              </button>
            )}

            {/* Mark as paid (for issued/overdue) */}
            {invoice.canMarkPaid && (
              <button
                onClick={handleMarkPaid}
                disabled={isActionLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" /> {t('invoices.markPaid')}
              </button>
            )}

            {/* Check Payment Status (for invoices with linked payment and pending status) */}
            {invoice.paymentId && ['ISSUED', 'OVERDUE'].includes(invoice.status) && (
              <button
                onClick={handleCheckStatus}
                disabled={isActionLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-blue-500/30 text-blue-500 rounded-lg font-medium hover:bg-blue-500/10 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${checkStatusMutation.isPending ? 'animate-spin' : ''}`} />
                {checkStatusMutation.isPending ? t('invoices.checkingStatus') : t('invoices.checkPaymentStatus')}
              </button>
            )}

            {/* Generate PDF */}
            {!invoice.hasPdf && invoice.status !== 'DRAFT' && (
              <button
                onClick={handleGeneratePdf}
                disabled={isActionLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-border-subtle rounded-lg font-medium hover:bg-hover disabled:opacity-50"
              >
                <FileText className="w-4 h-4" /> {t('invoices.generatePdf')}
              </button>
            )}

            {/* Download PDF with variant selector */}
            {invoice.hasPdf && (
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowVariantMenu(!showVariantMenu); }}
                  disabled={downloadPdfVariant.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-border-subtle rounded-lg font-medium hover:bg-hover disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  {downloadPdfVariant.isPending ? t('common.downloading') : t('invoices.downloadPdf')}
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showVariantMenu && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border-subtle rounded-lg shadow-lg z-10 overflow-hidden">
                    {downloadVariants.map((v) => (
                      <button
                        key={v.code}
                        onClick={(e) => { e.stopPropagation(); handleDownloadPdf(v.code); }}
                        className="w-full px-4 py-3 text-left hover:bg-hover border-b border-border-subtle last:border-b-0"
                      >
                        <div className="font-medium text-foreground text-sm">{v.name}</div>
                        <div className="text-xs text-muted">{v.description}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Print */}
            <button
              onClick={() => setShowPrintDialog(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-border-subtle rounded-lg font-medium hover:bg-hover"
            >
              <Printer className="w-4 h-4" /> {t('invoices.print')}
            </button>

            {/* Cancel (for non-terminal states) */}
            {invoice.canCancel && (
              <button
                onClick={handleCancel}
                disabled={isActionLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-red-500 border border-red-500/30 rounded-lg font-medium hover:bg-red-500/10 disabled:opacity-50"
              >
                <XCircle className="w-4 h-4" /> {t('invoices.cancel')}
              </button>
            )}
          </div>

          {/* Customer info */}
          <div className="bg-surface border border-border-subtle rounded-xl p-6">
            <h3 className="font-semibold text-foreground mb-4">{t('invoices.customer')}</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <Building className="w-4 h-4 text-muted mt-0.5" />
                <div>
                  <div className="font-medium text-foreground">{invoice.customerName}</div>
                  {invoice.customerLegalName && invoice.customerLegalName !== invoice.customerName && (
                    <div className="text-muted">{invoice.customerLegalName}</div>
                  )}
                </div>
              </div>
              {invoice.customerEmail && (
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-muted" />
                  <span className="text-muted">{invoice.customerEmail}</span>
                </div>
              )}
              {invoice.customerAddress && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-muted mt-0.5" />
                  <div className="text-muted">
                    {invoice.customerAddress}
                    {invoice.customerCity && <span>, {invoice.customerCity}</span>}
                    {invoice.customerCountry && <span>, {invoice.customerCountry}</span>}
                  </div>
                </div>
              )}
              {invoice.customerTaxId && (
                <div className="flex items-center gap-3">
                  <DollarSign className="w-4 h-4 text-muted" />
                  <span className="text-muted">TAX ID: {invoice.customerTaxId}</span>
                </div>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="bg-surface border border-border-subtle rounded-xl p-6">
            <h3 className="font-semibold text-foreground mb-4">{t('invoices.dates')}</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> {t('invoices.issueDate')}
                </span>
                <span className="text-foreground">{formatDate(invoice.issueDate)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted flex items-center gap-2">
                  <Clock className="w-4 h-4" /> {t('invoices.dueDate')}
                </span>
                <span className={invoice.isOverdue ? 'text-red-500 font-medium' : 'text-foreground'}>
                  {formatDate(invoice.dueDate)}
                </span>
              </div>
              {invoice.paidDate && (
                <div className="flex items-center justify-between">
                  <span className="text-muted flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> {t('invoices.paidDate')}
                  </span>
                  <span className="text-emerald-500">{formatDate(invoice.paidDate)}</span>
                </div>
              )}
              {invoice.daysUntilDue !== undefined && invoice.daysUntilDue > 0 && (
                <div className="text-xs text-muted mt-2">
                  {t('invoices.daysUntilDue', { days: invoice.daysUntilDue })}
                </div>
              )}
            </div>
          </div>

          {/* Organization Details */}
          <div className="bg-surface border border-border-subtle rounded-xl p-6">
            <h3 className="font-semibold text-foreground mb-4">{t('invoices.orgDetails')}</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted flex items-center gap-2">
                  <Hash className="w-4 h-4" /> {t('invoices.orgId')}
                </span>
                <span className="text-foreground font-mono text-xs">{invoice.orgId.slice(0, 8)}...</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> {t('invoices.billingCurrency')}
                </span>
                <span className="text-foreground font-semibold">{invoice.billingCurrency}</span>
              </div>
              {invoice.legalForm && (
                <div className="flex items-center justify-between">
                  <span className="text-muted flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> {t('invoices.legalForm')}
                  </span>
                  <span className="text-foreground">{invoice.legalForm}</span>
                </div>
              )}
              {invoice.capitalAmount && invoice.capitalCurrency && (
                <div className="flex items-center justify-between">
                  <span className="text-muted flex items-center gap-2">
                    <Landmark className="w-4 h-4" /> {t('invoices.capitalAmount')}
                  </span>
                  <span className="text-foreground">
                    {invoice.capitalAmount.toLocaleString()} {invoice.capitalCurrency}
                  </span>
                </div>
              )}
              {invoice.customerRccm && (
                <div className="flex items-center justify-between">
                  <span className="text-muted flex items-center gap-2">
                    <FileText className="w-4 h-4" /> {t('invoices.rccm')}
                  </span>
                  <span className="text-foreground font-mono text-xs">{invoice.customerRccm}</span>
                </div>
              )}
              {invoice.placeOfRegistration && (
                <div className="flex items-center justify-between">
                  <span className="text-muted flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> {t('invoices.placeOfRegistration')}
                  </span>
                  <span className="text-foreground">{invoice.placeOfRegistration}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Preview Modal - Theme-aware (dark → blackops, light → tactical) */}
      <InvoicePreviewModal
        invoiceId={invoice.id}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        onPrint={() => {
          setShowPrintDialog(true);
          setShowPreview(false);
        }}
      />

      {/* Print Invoice Dialog - Always uses stealth variant */}
      <PrintInvoiceDialog
        invoiceId={invoice.id}
        isOpen={showPrintDialog}
        onClose={() => setShowPrintDialog(false)}
      />
    </div>
  );
}
